import { useState, useEffect, useContext } from 'react'
import styles from './styles.module.css'
import { Input, Select } from '../../../components/ModalInputs'
import Button from '../../../components/Button'
import Table from '../../../components/Table'
import { StaffRosterContext } from '../../../utils/context'
import { useSelector } from 'react-redux'

const API_TAB = 'privileges'

// Данные для дропдаунов
const benefitTypes = [
  { name: '', value: '' },
  { name: 'Льгота на проезд', value: 'transport' },
  { name: 'Льгота на питание', value: 'food' },
  { name: 'Компенсация ЖКХ', value: 'housing' },
  { name: 'Льгота по оплате детского сада', value: 'kindergarten' },
  { name: 'Санаторно-курортное лечение', value: 'sanatorium' },
  { name: 'Материальная помощь', value: 'material_aid' },
  { name: 'Льгота многодетной семье', value: 'large_family' }
]

const basisTypes = [
  { name: '', value: '' },
  { name: 'Федеральный закон', value: 'federal_law' },
  { name: 'Региональный закон', value: 'regional_law' },
  { name: 'Приказ организации', value: 'organization_order' },
  { name: 'Коллективный договор', value: 'collective_agreement' },
  { name: 'Решение профсоюза', value: 'union_decision' }
]

export default function SocialBenefits() {
  const { allForms, trackDeletion } = useContext(StaffRosterContext)
  const { values, setFieldValue } = allForms

  // Данные из API (Redux)
  const { personTabData } = useSelector(state => state.personnel)
  const { socialBenefits: apiSocialBenefits } = personTabData || {}

  const [formData, setFormData] = useState({
    benefitType: '',
    documentNumber: '',
    documentDate: '',
    basisType: '',
    basisDetails: '',
    validFrom: '',
    validTo: ''
  })

  const [selectedRow, setSelectedRow] = useState(null)

  // Данные таблицы берем из Formik
  const tableData = values.socialBenefits || []

  // Синхронизация API данных с Formik (только при первой загрузке)
  useEffect(() => {
    if (apiSocialBenefits && !tableData.length) {
      setFieldValue('socialBenefits', apiSocialBenefits)
    }
  }, [apiSocialBenefits, tableData.length, setFieldValue])

  const handleFormChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
  }

  const handleRowSelect = (id) => {
    setSelectedRow(selectedRow === id ? null : id)
  }

  const handleAddRow = () => {
    const newRow = {
      id: `new_${Date.now()}`,
      benefit_type: formData.benefitType,
      document_number: formData.documentNumber,
      document_date: formData.documentDate,
      basis_type: formData.basisType,
      basis_details: formData.basisDetails,
      valid_from: formData.validFrom,
      valid_to: formData.validTo
    }
    // Сохраняем в Formik
    setFieldValue('socialBenefits', [...tableData, newRow])
  }

  const handleDeleteRow = () => {
    if (selectedRow !== null) {
      const rowToDelete = tableData.find(row => (row.id || row.xp_key) === selectedRow)
      if (rowToDelete) {
        const pk = rowToDelete.xp_key || rowToDelete.id || rowToDelete.xp_fm_id
        if (pk && !String(pk).startsWith('new_')) {
          trackDeletion(API_TAB, pk)
        }
      }
      // Обновляем в Formik
      const updatedData = tableData.filter(row => (row.id || row.xp_key) !== selectedRow)
      setFieldValue('socialBenefits', updatedData)
      setSelectedRow(null)
    }
  }

  const renderOptions = ({ name, value }) => (
    <option key={value || 'empty'} value={value}>{name}</option>
  )

  return (
    <div className={styles.tabWrapper}>
      {/* Форма */}
      <div className={styles.qualificationForm}>
        {/* Строка 1 */}
        <div className={styles.inputsRow}>
          <Select
            label="Наименование льготы"
            name="benefitType"
            value={formData.benefitType}
            onChange={handleFormChange}
          >
            {benefitTypes.map(renderOptions)}
          </Select>
        </div>

        {/* Строка 2 */}
        <div className={styles.inputsRow}>
          <Input
            label="Номер документа"
            name="documentNumber"
            value={formData.documentNumber}
            onChange={handleFormChange}
          />
          <Input
            label="Дата документа"
            type="date"
            name="documentDate"
            value={formData.documentDate}
            onChange={handleFormChange}
          />
        </div>

        {/* Строка 3 */}
        <div className={styles.inputsRow}>
          <Select
            label="Тип основания"
            name="basisType"
            value={formData.basisType}
            onChange={handleFormChange}
          >
            {basisTypes.map(renderOptions)}
          </Select>
          <Input
            label="Основание (подробно)"
            name="basisDetails"
            value={formData.basisDetails}
            onChange={handleFormChange}
          />
        </div>

        {/* Строка 4 */}
        <div className={styles.inputsRow}>
          <Input
            label="Действует с"
            type="date"
            name="validFrom"
            value={formData.validFrom}
            onChange={handleFormChange}
          />
          <Input
            label="Действует по"
            type="date"
            name="validTo"
            value={formData.validTo}
            onChange={handleFormChange}
          />
          <div className={styles.qualificationButtonsInline}>
            <Button onClick={handleAddRow}>+ Добавить строку</Button>
            <Button mode="delete" onClick={handleDeleteRow}>× Удалить строку</Button>
          </div>
        </div>
      </div>

      {/* Таблица */}
      <div className={styles.qualificationTableSection}>
        <Table>
          <thead>
            <tr>
              <th>Наименование льготы</th>
              <th>Номер документа</th>
              <th>Дата документа</th>
              <th>Основание</th>
            </tr>
          </thead>
          <tbody>
            {tableData.length > 0 ? (
              tableData.map(row => {
                const rowId = row.id || row.xp_key
                return (
                  <tr
                    key={rowId}
                    onClick={() => handleRowSelect(rowId)}
                    className={selectedRow === rowId ? styles.selectedRow : ''}
                  >
                    <td>{row.benefit_type || row.benefitName}</td>
                    <td>{row.document_number || row.documentNumber}</td>
                    <td>{row.document_date || row.documentDate}</td>
                    <td>{row.basis_details || row.basis}</td>
                  </tr>
                )
              })
            ) : (
              <tr>
                <td colSpan="4" className={styles.emptyTable}>
                  Нет данных о социальных льготах
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>
    </div>
  )
}
